import { ParticipantStatus } from "../types/PersologOnlineTypes";
import type { AssociatedDirectInput, Participant } from "../types/PersologOnlineTypes";

/** The neutral benchmark range applied when a competency has no persisted direct input. */
export const DEFAULT_BENCHMARK_RANGE: [number, number] = [12.5, 87.5];

/** A value that a router query key can hold. */
type QueryValue = string | number | null | undefined | Array<string | null>;

/**
 * Returns the benchmark range for a competency taken from the direct input, or the default
 * [12.5, 87.5] when there is no direct input or the competency is not present.
 */
export function getRangeFromDirectInput(
    directInput: AssociatedDirectInput | null | undefined,
    competencyName: string,
): number[] {
    if (directInput) {
        return (
            directInput.input.dimensions[0].competencies.find(
                (competency) => competency.name === competencyName,
            )?.values ?? [...DEFAULT_BENCHMARK_RANGE]
        );
    }
    return [...DEFAULT_BENCHMARK_RANGE];
}

/** True when a participant has reached a state in which a profile exists (generated or notified). */
export function isGeneratedStatus(status: Participant["status"]): boolean {
    return status === ParticipantStatus.GENERATED || status === ParticipantStatus.NOTIFIED;
}

/** The participants that already have a generated (or notified) profile. */
export function getGeneratedParticipants(participants: Participant[]): Participant[] {
    return participants.filter((participant) => isGeneratedStatus(participant.status));
}

/** Whether any participant already has a generated (or notified) profile. */
export function hasGeneratedParticipants(participants: Participant[]): boolean {
    return participants.some((participant) => isGeneratedStatus(participant.status));
}

/**
 * Which PDF an email deep link wants to open:
 * - a specific `chartId` → the group chart,
 * - a `participantId` equal to the project id → the (legacy) group chart,
 * - any other `participantId` → that participant's profile,
 * - nothing relevant → null.
 */
export function resolvePdfLinkType(
    queryChartId: QueryValue,
    queryParticipantId: QueryValue,
    projectId: number | undefined,
): "group-chart" | "profile" | null {
    if (queryChartId !== undefined) {
        return "group-chart";
    }
    if (queryParticipantId !== undefined) {
        return Number(queryParticipantId) === projectId ? "group-chart" : "profile";
    }
    return null;
}

/**
 * Picks the profile language to open: the requested language (case-insensitive) when it was
 * generated, otherwise the first available language.
 */
export function pickProfileLanguage(languages: string[], requestedLanguage: string | undefined): string {
    return (
        languages.find((lang) => lang.toLowerCase() === requestedLanguage?.toLowerCase()) ?? languages[0]
    );
}

/**
 * Key for the profile-view confirmation dialog: the chartId for group-chart links, otherwise the
 * participantId (profile links), defaulting to "0".
 */
export function computeConfirmationDialogKey(chartId: QueryValue, participantId: QueryValue): string {
    return (chartId ?? participantId)?.toString() ?? "0";
}
